/**
 * Connected AI tools (TMMAPP-0142 U7 §L; TMMAPI-0183 U1, MM-D-148): a person's AI tools (ChatGPT,
 * Claude, …) signed in through our OAuth. A tool reads nothing until the person grants it a session,
 * Read only (`READ`) or Read and write (`WRITE`, a subscriber's), per session and per connection; it
 * then acts as the person inside that session's scope.
 *
 * - `GET /v1/me/agent-connections`: each connection (oldest first) with its grants.
 * - `PATCH …/{id} {label}`: rename (1–80 characters; the server's `400 VALIDATION_FAILED`).
 * - `PUT …/{id}/grants/{sessionId} {access}` grants or changes a session; `DELETE …/grants/{sessionId}`
 *   is Off (idempotent).
 * - `DELETE …/{id}` disconnects: its grants go, and the tool reaches nothing.
 * - A grant on an archived session (`archived`, TMMAPI-0185 U1) is read-only for the tool whatever
 *   its access, until the session is restored.
 *
 * Free and shown to every tier; nothing here is AI, and nothing is stored in the browser.
 */
import { request } from './client';
import type {
  AgentConnection as WireConnection,
  AgentConnectionGrant as WireGrant,
  AgentConnectionPatchRequest,
  AgentGrantRequest,
} from './generated';

/** `READ`: Read only; `WRITE`: Read and write. Off is no grant. */
export type AgentAccess = 'READ' | 'WRITE';
/** The three-way choice the screens offer. */
export type AgentAccessChoice = 'OFF' | AgentAccess;

export interface AgentConnectionGrant {
  sessionId: number;
  sessionName: string;
  access: AgentAccess;
  /** The session is archived: read-only for the tool, and its access can't change here. */
  archived: boolean;
}

export interface AgentConnection {
  id: number;
  /** The person's name for it (the platform's name, or "AI tool" and an id prefix, until renamed). */
  label: string;
  firstSeenAt: string;
  lastUsedAt: string;
  /** The sessions it may use; empty: it reads nothing. */
  grants: AgentConnectionGrant[];
}

/** The longest label the server takes. */
export const AGENT_LABEL_MAX = 80;

export function accessOf(value: string): AgentAccess {
  return value === 'WRITE' ? 'WRITE' : 'READ';
}

function normalizeGrant(w: WireGrant): AgentConnectionGrant {
  return {
    sessionId: w.sessionId,
    sessionName: w.sessionName,
    access: accessOf(w.access),
    archived: w.archived,
  };
}

export function normalizeAgentConnection(w: WireConnection): AgentConnection {
  return {
    id: w.id,
    label: w.label,
    firstSeenAt: w.firstSeenAt,
    lastUsedAt: w.lastUsedAt,
    grants: w.grants.map(normalizeGrant),
  };
}

const BASE = '/v1/me/agent-connections';

export const agentConnectionsApi = {
  async list(): Promise<AgentConnection[]> {
    const wire = await request<WireConnection[]>('GET', BASE);
    return wire.map(normalizeAgentConnection);
  },
  async rename(connectionId: number, label: string): Promise<AgentConnection> {
    const json: AgentConnectionPatchRequest = { label };
    return normalizeAgentConnection(
      await request<WireConnection>('PATCH', `${BASE}/${connectionId}`, { json }),
    );
  },
  /** Off revokes (`DELETE`, idempotent); Read only and Read and write grant or change (`PUT`). */
  async setAccess(
    connectionId: number,
    sessionId: number,
    choice: AgentAccessChoice,
  ): Promise<void> {
    if (choice === 'OFF') {
      await request<unknown>('DELETE', `${BASE}/${connectionId}/grants/${sessionId}`);
      return;
    }
    const json: AgentGrantRequest = { access: choice };
    await request<WireConnection>('PUT', `${BASE}/${connectionId}/grants/${sessionId}`, { json });
  },
  async disconnect(connectionId: number): Promise<void> {
    await request<unknown>('DELETE', `${BASE}/${connectionId}`);
  },
};
